import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Lock, User } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

// Import our new authentication hook
import { useAdminAuth } from '@/hooks/useAdminAuth';

const AdminAuth = () => {
  const [password, setPassword] = useState('');
  const [adminName, setAdminName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  
  // Mock functions for the hook - these will be replaced when the admin is authenticated
  const recordAction = () => {};
  const removeActiveAdmin = async () => {};
  
  // Use our new authentication hook
  const { verifyPassword } = useAdminAuth(recordAction, removeActiveAdmin, adminName);

  // Check if user is already authenticated
  useEffect(() => {
    const isAuthenticated = localStorage.getItem('adminAuthenticated') === 'true';
    const storedAdminName = localStorage.getItem('adminName');
    const authTimestamp = parseInt(localStorage.getItem('adminAuthTimestamp') || '0');
    const currentTime = Date.now();
    
    // Session expires after 4 hours
    const sessionExpired = currentTime - authTimestamp > 4 * 60 * 60 * 1000;
    
    if (storedAdminName) {
      setAdminName(storedAdminName);
    }
    
    if (isAuthenticated && !sessionExpired) {
      navigate('/admin-dashboard');
    } else if (sessionExpired && isAuthenticated) {
      // Clear expired session
      localStorage.removeItem('adminAuthenticated');
      localStorage.removeItem('adminAuthTimestamp');
      toast({
        title: "Phiên đăng nhập hết hạn",
        description: "Vui lòng đăng nhập lại để tiếp tục",
        variant: "destructive"
      });
    }
  }, [navigate, toast]);

  // Get client information (for logging login attempts)
  const getClientInfo = () => {
    const userAgent = navigator.userAgent;
    let browser = "Unknown";
    let os = "Unknown";
    let device = "Unknown";
    
    // Detect browser
    if (userAgent.indexOf("Firefox") > -1) {
      browser = "Firefox";
    } else if (userAgent.indexOf("SamsungBrowser") > -1) {
      browser = "Samsung Browser";
    } else if (userAgent.indexOf("Opera") > -1 || userAgent.indexOf("OPR") > -1) {
      browser = "Opera";
    } else if (userAgent.indexOf("Trident") > -1) {
      browser = "Internet Explorer";
    } else if (userAgent.indexOf("Edge") > -1) {
      browser = "Edge";
    } else if (userAgent.indexOf("Chrome") > -1) {
      browser = "Chrome";
    } else if (userAgent.indexOf("Safari") > -1) {
      browser = "Safari";
    }
    
    // Detect OS
    if (userAgent.indexOf("Win") > -1) {
      os = "Windows";
    } else if (userAgent.indexOf("Mac") > -1) {
      os = "MacOS";
    } else if (userAgent.indexOf("Android") > -1) {
      os = "Android";
    } else if (userAgent.indexOf("like Mac") > -1) {
      os = "iOS";
    } else if (userAgent.indexOf("Linux") > -1) {
      os = "Linux";
    }
    
    // Detect device type
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(userAgent)) {
      device = "Tablet";
    } else if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(userAgent)) {
      device = "Mobile";
    } else {
      device = "Desktop";
    }
    
    return {
      userAgent,
      browser,
      os,
      device
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const isValidPassword = await verifyPassword(password);
      
      if (isValidPassword) {
        const adminId = localStorage.getItem('adminId') || uuidv4();
        const trimmedName = adminName.trim() || `Admin-${adminId.slice(0, 6)}`;
        
        localStorage.setItem('adminAuthenticated', 'true');
        localStorage.setItem('adminName', trimmedName);
        localStorage.setItem('adminId', adminId);
        localStorage.setItem('adminAuthTimestamp', Date.now().toString());
        
        // Record login action with client info
        const storedActions = localStorage.getItem('adminActions') || '[]';
        const adminActions = JSON.parse(storedActions);
        const clientInfo = getClientInfo();
        
        const newAction = {
          actionId: uuidv4(),
          actionType: 'login',
          performedBy: trimmedName,
          timestamp: new Date().toISOString(),
          details: 'Admin login successful',
          clientInfo
        };
        
        localStorage.setItem('adminActions', JSON.stringify([newAction, ...adminActions].slice(0, 500)));
        
        toast({
          title: "Đăng nhập thành công",
          description: `Chào mừng ${trimmedName} đến trang quản trị`,
        });
        navigate('/admin-dashboard');
      } else {
        toast({
          title: "Đăng nhập thất bại",
          description: "Mật khẩu không chính xác",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Login error:', error);
      toast({
        title: "Đăng nhập thất bại",
        description: "Có lỗi xảy ra, vui lòng thử lại",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-blue-50 to-white p-4">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="space-y-1 text-center">
          <CardTitle className="text-2xl font-bold">Đăng nhập Admin</CardTitle>
          <CardDescription>
            Nhập tên và mật khẩu để truy cập trang quản trị
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-4">
              <div className="relative">
                <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Tên của bạn"
                  value={adminName}
                  onChange={(e) => setAdminName(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  type="password"
                  placeholder="Mật khẩu"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <Button 
              type="submit" 
              className="w-full" 
              disabled={isLoading || !password}
            >
              {isLoading ? "Đang xử lý..." : "Đăng nhập"}
            </Button>
          </form>
        </CardContent>
        <CardFooter className="text-center text-sm text-muted-foreground">
          <div className="w-full">
            <Button 
              variant="ghost" 
              className="w-full text-sm" 
              onClick={() => navigate('/')}
            >
              Quay lại trang chủ
            </Button>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
};

export default AdminAuth;
